import { GrooveConsole } from './rhythm/GrooveConsole';
import { RhythmState } from './rhythm/RhythmState';
import { PatternGenerator } from './rhythm/PatternGenerator';
import { GrooveLock, GrooveContext } from './rhythm/GrooveLock';
import { CadenceType } from './StructureEngine';

export class RhythmEngine {
    private grooveConsole: GrooveConsole;
    private rhythmState: RhythmState;
    private patternGenerator: PatternGenerator;

    // V11: Groove Lock for tempo detection and magnetic quantization
    private grooveLock: GrooveLock;

    constructor() {
        this.grooveConsole = new GrooveConsole();
        this.rhythmState = new RhythmState();
        this.patternGenerator = new PatternGenerator(this.grooveConsole);
        this.grooveLock = new GrooveLock();
        this.reset();
    }

    reset() {
        this.grooveConsole.reset();
        this.rhythmState.reset();
        this.patternGenerator.reset();
        this.grooveLock.reset();
    }

    /**
     * Returns a dynamic tempo multiplier based on user activity AND Rubato.
     * < 0.8 = Slow/Rubato (Low density)
     * 1.0 = Normal
     * > 1.2 = Accelerando (High density)
     * 
     * P0 FIX: Added ritardandoFactor parameter for natural song endings
     * @param ritardandoFactor - Optional multiplier (0.7-1.0) from StructureEngine for finale slowdown
     */
    public getAdaptiveTempoMultiplier(ritardandoFactor: number = 1.0): number {
        // V11: Get detected BPM from GrooveLock
        const grooveCtx = this.getGrooveContext();
        // Only use detected BPM if confidence is high (> 0.5)
        const reliableBPM = grooveCtx.confidence > 0.5 ? grooveCtx.detectedBPM : null;

        const baseMultiplier = this.grooveConsole.getAdaptiveTempoMultiplier(reliableBPM);

        // Apply ritardando: slower tempo = lower multiplier
        return baseMultiplier * ritardandoFactor;
    }

    incrementTouch() {
        this.grooveConsole.trackTouch();
        this.rhythmState.incrementTouch();
    }

    /**
     * Determines if a chord should be triggered for this touch.
     * Returns the decision and an optional duration for sustained chords.
     */
    shouldTriggerChord(textureMode: 'melodic' | 'chordal' | 'mixed', dynamics?: { velocity: number; yPos: number }): { shouldPlay: boolean, duration?: number } {
        return this.rhythmState.shouldTriggerChord(textureMode, dynamics);
    }

    /**
     * Checks if we should advance to the next chord in the progression.
     * Automatically updates the internal counter.
     */
    /**
     * Checks if we should advance to the next chord in the progression.
     * Automatically updates the internal counter.
     */
    shouldAdvanceProgression(force: boolean = false, targetDuration: number = 8): boolean {
        return this.rhythmState.shouldAdvanceProgression(force, targetDuration);
    }

    /**
     * V5: Helper for Lookahead (Secondary Dominants)
     * Returns 0.0 to 1.0 indicating how close we are to the next chord change.
     */
    getProgressionProgress(): number {
        return this.rhythmState.getProgressionProgress();
    }

    /**
     * HUMANIZATION: Dynamics
     * Adds random variation to velocity to sound less robotic.
     * Accents the 'one' (or strong beats) if specified.
     */
    humanizeVelocity(baseVelocity: number, isStrongBeat: boolean = false): number {
        return this.grooveConsole.humanizeVelocity(baseVelocity, isStrongBeat);
    }

    /**
     * V11 ENHANCED: HUMANIZATION with Groove Lock
     * Adds subtle timing offsets + magnetic pull toward detected beat grid.
     * @param instrumentRole 'bass' | 'chord' | 'melody'
     */
    humanizeTiming(baseDelay: number, instrumentRole: 'bass' | 'chord' | 'melody' = 'melody'): number {
        // AUDIT FIX 1.1: Apply magnetic pull BEFORE humanization to avoid fighting
        let adjustedBase = baseDelay;
        const grooveContext = this.getGrooveContext();

        // V11: Apply magnetic pull from GrooveLock
        if (grooveContext.confidence > 0.4 && !grooveContext.isSyncopatedTouch) {
            // Convert magnetic pull from ms to seconds for delay adjustment
            adjustedBase += grooveContext.magneticPull / 1000;
        }

        // V11: Stretch bass notes during ritardando
        if (grooveContext.isRitardando && instrumentRole === 'bass') {
            adjustedBase += 0.05; // Add subtle lag for expressive rubato
        }

        // Apply humanization to the grid-aligned time
        return this.grooveConsole.humanizeTiming(adjustedBase, instrumentRole);
    }

    /**
     * PATTERN GENERATOR
     * Converts raw chord/bass/melody notes into scheduled Playable Notes.
     * FEATURE 3: Rhythmic DNA
     * Uses persistent rhythmic cells to create "hooks".
     * V10: Added density for Rhythmic Breathing.
     */
    public generatePattern(
        chordNotes: number[],
        bassNote: number,
        melodyNote: number,
        context: {
            section: 'Verse' | 'Chorus' | 'Bridge',
            tempoMult: number,
            baseVelocity: number,
            sectionDynamics: number,
            tuning: number,
            currentKeyOffset?: number,
            timbre?: any,
            density?: 'sparse' | 'normal' | 'dense',  // V10
            cadenceType?: CadenceType,  // P3 FIX
            phraseFunction?: 'Tension' | 'Resolution' | 'Neutral'  // P3 FIX
        }
    ): Array<{ freq: number, delay: number, velocity: number, duration?: number, timbre?: any }> {
        return this.patternGenerator.generatePattern(chordNotes, bassNote, melodyNote, context);
    }

    // =====================================================
    // V11: GROOVE LOCK METHODS
    // =====================================================

    /**
     * V11: Record a touch for groove detection.
     * Call this on each touch to build the groove profile.
     */
    recordTouchForGroove(): GrooveContext {
        return this.grooveLock.recordTouch();
    }

    /**
     * V11: Get current groove context without recording a new touch.
     * AUDIT FIX 1.4: Now uses cached context to prevent BPM pollution.
     */
    getGrooveContext(): GrooveContext {
        return this.grooveLock.getContext();
    }

    /**
     * V11: Get detected BPM (null if not confident).
     */
    getDetectedBPM(): number | null {
        return this.grooveLock.getDetectedBPM();
    }

    /**
     * V11: Check if groove is locked (confident tempo detected).
     */
    isGrooveLocked(): boolean {
        return this.grooveLock.isGrooveLocked();
    }

    /**
     * AUDIT FIX: Set the song-level DNA for persistent rhythmic identity
     * This establishes the groove pattern for the entire song
     */
    setSongDNA(dna: import('./ComposerSession').SongDNA): void {
        this.patternGenerator.setSongDNA(dna);
    }
}

